import { getOverlayPortalParent } from '@spark';

/**
 * Marks the app's page scroller (`app/layout/SideBar.jsx`). A coach mark whose
 * anchor scrolls with that element renders *inside* it, absolutely
 * positioned, so the browser moves the outline and the bubble together with
 * the anchor in the same frame — no re-measure lag while scrolling.
 */
export const WALKTHROUGH_SCROLLER_ATTR = 'data-walkthrough-scroller';
export const WALKTHROUGH_SCROLLER_SELECTOR = `[${WALKTHROUGH_SCROLLER_ATTR}]`;

const isSticky = (node) => {
    try {
        return window.getComputedStyle(node).position === 'sticky';
    } catch {
        return false;
    }
};

/** True when `element` or an ancestor below `scroller` is `position: sticky`: it does not move with the content. */
const pinnedInside = (element, scroller) => {
    for (let node = element; node && node !== scroller; node = node.parentElement) {
        if (isSticky(node)) return true;
    }
    return false;
};

/**
 * Where a coach mark for `targetElement` renders, and in which coordinate
 * space:
 * - `native`: the anchor lives in the page scroller and moves with its
 *   content, so the mark is positioned absolutely inside that scroller.
 * - `fixed`: anchors outside the scroller (modals, tests) or pinned inside it
 *   (sticky headings) are positioned against the viewport and portaled to the
 *   Spark overlay root; only the JS re-measure follows those.
 */
export function resolveOverlayHost(targetElement) {
    const scroller = targetElement?.closest?.(WALKTHROUGH_SCROLLER_SELECTOR) ?? null;
    if (scroller instanceof HTMLElement && !pinnedInside(targetElement, scroller)) {
        return { mode: 'native', host: scroller };
    }
    return { mode: 'fixed', host: getOverlayPortalParent(targetElement) };
}

/** The scroller's visible padding box in viewport coordinates — what the bubble has to stay inside. */
export function scrollerViewport(host) {
    const box = host.getBoundingClientRect();
    return {
        top: box.top + host.clientTop,
        left: box.left + host.clientLeft,
        width: host.clientWidth,
        height: host.clientHeight,
    };
}

/** Translate a viewport point into the scroller's content coordinates (where its `position: absolute` children live). */
export function toHostCoords({ top, left }, host, viewport = scrollerViewport(host)) {
    return { top: top - viewport.top + host.scrollTop, left: left - viewport.left + host.scrollLeft };
}
